// Tip breakdown, task thresholds and the forgone tip (Feature P1). Pure
// functions over a JobResult / task pct: no sim state, no formula changes.
// The breakdown re-reads the numbers `result()` already produced and adds
// reasons, so fee + tip − deductions + write-off === totalAud, always.

import type { JobResult, PropKind, ZoneId } from './types';
import { MULCH_PENALTY_AUD, OBSTACLE_PENALTY_AUD, VIOLATION_PENALTY_AUD, tipPct } from './contract';
import { hitPenaltyAud, hitReason, propShortName, violationReason } from './props';
import { chuteReason } from './chute';
import { STRIPE_TIER_COPY, patternName, stripeTier, type StripeTier } from './stripes';
import { COUNCIL_COPY } from './debris';
import { EXTRA_MILE_COPY, HONEST_BLOKE_COPY, bonusAud } from './vibe';

// ─── HUD thresholds ─────────────────────────────────────────────────────

export const INVOICE_AT_PCT = 90;
export const EXTRA_MILE_AT_PCT = 98;

export type ThresholdState = 'working' | 'invoice' | 'extra-mile';

export const THRESHOLD_COPY: Record<ThresholdState, string> = {
  working: '',
  invoice: 'Done enough to invoice',
  'extra-mile': 'Extra mile',
};

/** < 90 working, 90 to < 98 amber "Done enough to invoice", ≥ 98 green "Extra mile". */
export function taskThreshold(pct: number): ThresholdState {
  if (pct >= EXTRA_MILE_AT_PCT) return 'extra-mile';
  if (pct >= INVOICE_AT_PCT) return 'invoice';
  return 'working';
}

// ─── Forgone tip ────────────────────────────────────────────────────────

/** The tip `result()` would pay on this fee at this thoroughness (same rounding). */
export function tipAudAt(feeAud: number, thoroughness: number): number {
  return Math.round((feeAud * tipPct(thoroughness)) / 100);
}

export interface KnockOff {
  /** The sim's invoice gate: every task at its contract target (ALL DONE). */
  enabled: boolean;
  state: ThresholdState;
  tipPct: number;
  tipAud: number;
  /** Tip at 100% thoroughness. */
  bestTipAud: number;
  /** bestTipAud − tipAud: what knocking off now leaves on the table. */
  forgoneAud: number;
}

/** Knock off now vs. go the extra mile to 100%. */
export function knockOff(thoroughness: number, feeAud: number, finished: boolean): KnockOff {
  const tipAud = tipAudAt(feeAud, thoroughness);
  const bestTipAud = tipAudAt(feeAud, 100);
  return {
    enabled: finished,
    state: taskThreshold(thoroughness),
    tipPct: tipPct(thoroughness),
    tipAud,
    bestTipAud,
    forgoneAud: Math.max(0, bestTipAud - tipAud),
  };
}

/** Mean task pct, as `result()` computes thoroughness. */
export function meanPct(tasks: readonly { pct: number }[]): number {
  return tasks.length ? tasks.reduce((s, t) => s + t.pct, 0) / tasks.length : 100;
}

// ─── Tip breakdown ──────────────────────────────────────────────────────

export interface DeductionLine {
  key: string;
  /** "clipped the fence" */
  reason: string;
  count: number;
  eachAud: number;
  /** count × eachAud */
  aud: number;
  severity: 'minor' | 'major';
  /** Prop kind (prop lines) or ground-object kind (mulch lines), for copy. */
  kind?: string;
  /** "3 × clipped the fence" */
  label: string;
}

export type TipOutcome = 'abandoned' | 'wiped' | 'major' | 'rough' | 'extra-mile' | 'tipped' | 'no-tip';

export interface TipBreakdown {
  thoroughness: number;
  feeAud: number;
  tipPct: number;
  tipAud: number;
  /**
   * Feature P5: the stripe line, when the result carries a paid bonus
   * (`stripeBonusPct` set). Added after the tip; 0 when absent.
   */
  stripe?: { score: number; tier: StripeTier; copy: string; bonusPct: number; pattern?: string };
  stripeBonusAud: number;
  /**
   * Feature P6: leaves blown off the property on a clear job. Counted as
   * cleared, less `nudgePct` on the clear task (already in thoroughness and
   * the tip); `copy` is "Council won't love that." Absent otherwise.
   */
  council?: { leaves: number; nudgePct: number; copy: string };
  /**
   * Feature P9: money on top — wallets handed back ("honest bloke") and the
   * extra mile. Each its own line; `extrasAud` is their sum (0 when none).
   */
  extras: { key: 'honest-bloke' | 'extra-mile'; label: string; aud: number; rep?: number }[];
  extrasAud: number;
  deductions: DeductionLine[];
  /** Sum of deductions === result.penaltiesAud. */
  deductionsAud: number;
  /** What the owner let slide when damage ate more than fee + tip + stripe bonus (total floors at 0). */
  writeOffAud: number;
  totalAud: number;
  outcome: TipOutcome;
  copy: string;
}

function bump(map: Map<string, DeductionLine>, key: string, reason: string, eachAud: number, severity: 'minor' | 'major', kind?: string): void {
  const line = map.get(key);
  if (line) {
    line.count++;
    line.aud += eachAud;
  } else {
    map.set(key, { key, reason, count: 1, eachAud, aud: eachAud, severity, kind, label: '' });
  }
}

const MAJOR_COPY: Record<string, string> = {
  'ran over the wicket': "Ran over the wicket. The curator's not speaking to you.",
  'scalped the bowls green': "Scalped the green. The bowls club's forming a committee.",
  'nicked the pool': "Nicked the pool. Owner's gone very quiet.",
  'dinged the car': "Dinged the car. They've taken a photo for the insurance.",
  'dinged the ute': "Dinged the ute. He's had it since '98, mate.",
};

/**
 * Reasons × counts for everything `penaltiesAud` charged, plus the outcome
 * line. Older results without `hits` / `violationZones` fall back to
 * generic reasons; any remainder is one "other damage" line so the sum holds.
 */
export function tipBreakdown(r: JobResult): TipBreakdown {
  const lines = new Map<string, DeductionLine>();

  const zones: (ZoneId | undefined)[] = r.violationZones?.length === r.violations
    ? r.violationZones
    : Array.from({ length: r.violations }, () => undefined);
  for (const z of zones) {
    const reason = violationReason(z);
    bump(lines, `zone:${reason}`, reason, VIOLATION_PENALTY_AUD, 'major');
  }

  const hits = r.hits && r.hits.length === r.obstacleHits
    ? r.hits
    : Array.from({ length: r.obstacleHits }, () => null);
  // Majors first so the worst news leads.
  const ordered = [...hits].sort((a, b) => Number(b?.damage === 'major') - Number(a?.damage === 'major'));
  for (const h of ordered) {
    if (!h) bump(lines, 'prop:?', 'hit something', OBSTACLE_PENALTY_AUD, 'minor');
    // Keyed by reason, so Colorbond and boundary fence both count as "clipped the fence".
    else {
      // Feature P2: a chute hit reads "chute dragged along the fence", its own line.
      const reason = h.via === 'chute' ? chuteReason(h.kind) : hitReason(h.kind);
      bump(lines, `prop:${reason}:${h.damage}`, reason, hitPenaltyAud(h.damage), h.damage, h.kind);
    }
  }

  const mulched = r.mulched ?? 0;
  for (let i = 0; i < mulched; i++) {
    const kind = r.mulchedKinds?.[i];
    // Feature P9: a named item reads as itself ("mulched the thong").
    const name = r.mulchedNames?.[i] ?? kind?.replace(/-/g, ' ');
    const reason = name ? `mulched the ${name}` : 'mulched something';
    const tag = r.mulchedNames?.[i] ?? kind;
    bump(lines, `mulch:${tag ?? '?'}`, reason, MULCH_PENALTY_AUD, 'minor', tag);
  }

  const deductions = [...lines.values()];
  let deductionsAud = deductions.reduce((s, l) => s + l.aud, 0);
  const rest = r.penaltiesAud - deductionsAud;
  if (rest !== 0) {
    deductions.push({ key: 'other', reason: 'other damage', count: 1, eachAud: rest, aud: rest, severity: 'minor', label: '' });
    deductionsAud += rest;
  }
  for (const l of deductions) l.label = l.count > 1 || l.key !== 'other' ? `${l.count} × ${l.reason}` : l.reason;

  const stripeBonusAud = r.stripeBonusPct !== undefined ? r.stripeBonusAud ?? 0 : 0;
  const extras = extrasOf(r);
  const extrasAud = bonusAud(r);
  const writeOffAud = r.totalAud - (r.feeAud + r.tipAud + stripeBonusAud + extrasAud - deductionsAud);
  const outcome = outcomeOf(r, deductions, writeOffAud);
  const pattern = patternName(r);
  const stripe = r.stripeBonusPct !== undefined && r.stripeScore !== undefined
    ? { score: r.stripeScore, tier: stripeTier(r.stripeScore), copy: STRIPE_TIER_COPY[stripeTier(r.stripeScore)], bonusPct: r.stripeBonusPct, ...(pattern ? { pattern } : {}) }
    : undefined;
  return {
    thoroughness: r.thoroughness,
    feeAud: r.feeAud,
    tipPct: r.tipPct,
    tipAud: r.tipAud,
    ...(stripe ? { stripe } : {}),
    stripeBonusAud,
    ...(r.offProperty ? { council: { ...r.offProperty, copy: COUNCIL_COPY } } : {}),
    extras,
    extrasAud,
    deductions,
    deductionsAud,
    writeOffAud,
    totalAud: r.totalAud,
    outcome,
    copy: copyFor(outcome, deductions, r),
  };
}

/** Feature P9: the honest-bloke and extra-mile lines, when the result carries them. */
function extrasOf(r: JobResult): TipBreakdown['extras'] {
  const out: TipBreakdown['extras'] = [];
  if (r.honestBlokeAud) {
    const n = r.wallets ?? 1;
    out.push({ key: 'honest-bloke', label: `${n > 1 ? `${n} wallets` : 'Wallet'} handed back · ${HONEST_BLOKE_COPY}`, aud: r.honestBlokeAud });
  }
  if (r.extraMileAud) out.push({ key: 'extra-mile', label: EXTRA_MILE_COPY, aud: r.extraMileAud, ...(r.extraMileRep ? { rep: r.extraMileRep } : {}) });
  return out;
}

function outcomeOf(r: JobResult, deductions: DeductionLine[], writeOffAud: number): TipOutcome {
  if (r.abandoned) return 'abandoned';
  if (writeOffAud > 0) return 'wiped';
  if (deductions.some((l) => l.severity === 'major')) return 'major';
  if (deductions.length) return 'rough';
  if (r.thoroughness >= EXTRA_MILE_AT_PCT) return 'extra-mile';
  if (r.tipAud > 0) return 'tipped';
  return 'no-tip';
}

/** Most-billed minor line's short name ("fence"), for "Bit rough on the fence, mate". */
function roughOn(deductions: DeductionLine[], r: JobResult): string {
  const top = [...deductions].sort((a, b) => b.aud - a.aud)[0];
  if (!top) return 'place';
  if (top.kind && top.key.startsWith('prop:')) return propShortName(top.kind as PropKind);
  if (top.kind && top.key.startsWith('mulch:')) return top.kind.replace(/-/g, ' ');
  return r.layoutKey.includes('oval') ? 'oval' : 'yard';
}

function copyFor(outcome: TipOutcome, deductions: DeductionLine[], r: JobResult): string {
  switch (outcome) {
    case 'abandoned': return 'Half a job, half a cheque. No tip in that.';
    case 'wiped': return "Damage ate the lot. Owner's calling it square.";
    case 'major': {
      const worst = deductions.find((l) => l.severity === 'major');
      return (worst && MAJOR_COPY[worst.reason]) ?? 'Did some real damage out there. Word gets around.';
    }
    case 'rough': return `Bit rough on the ${roughOn(deductions, r)}, mate.`;
    case 'extra-mile': return "Neat as a pin — they've told the neighbours.";
    case 'tipped': return "Tidy job. They'll have you back.";
    case 'no-tip': return "Done enough to invoice. Nobody's framing it.";
  }
}
